import React, { useEffect, useRef, useState, useCallback } from 'react';
import { View, Text, FlatList, Alert, Linking, Platform, TouchableOpacity, Image, RefreshControl, Keyboard, InteractionManager } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '@/providers/AuthProvider';
import { useThemeColors } from '@/providers/ThemeProvider';
import { useMessages } from '@/modules/chat/hooks/useMessages';
import { useChat } from '@/modules/chat/hooks/useChat';
import { useChatActions } from '@/modules/chat/hooks/useChatActions';
import { useTypingIndicator } from '@/modules/chat/hooks/useTypingIndicator';
import { MessageBubble } from '@/modules/chat/components/MessageBubble';
import { MessageInput } from '@/modules/chat/components/MessageInput';
import { Message } from '@/modules/chat/types';
import { Loader } from '@/components/Loader';
import { groupMessagesByDate } from '@/modules/chat/utils/chatHelpers';
import { CreatePollModal } from '@/modules/polls';
import { EmojiReactionPicker } from '@/modules/chat/components/EmojiReactionPicker';

export default function ChatScreen() {
  const colors = useThemeColors();
  const router = useRouter();
  const { chatId } = useLocalSearchParams<{ chatId: string }>();
  const { user, activeProfile } = useAuth();
  const userId = activeProfile?.id || user?.id;
  const isPlayingAsDependent = activeProfile?.id && activeProfile?.id !== user?.id;

  const flatListRef = useRef<FlatList>(null);
  const [showPollModal, setShowPollModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [reactionMessage, setReactionMessage] = useState<Message | null>(null);

  const {
    messages,
    loading,
    error,
    hasMore,
    loadMore,
    markAsRead,
    refetch,
    addOptimisticMessage,
    updateMessage,
  } = useMessages(chatId, userId);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  const { chat, getChatName, getChatAvatar } = useChat(chatId, userId);
  const { deleteMessage, reactToMessage } = useChatActions(chatId, userId!);
  const { typingText } = useTypingIndicator(chatId, userId!);

  // Get chat display info
  const chatName = getChatName();
  const chatAvatar = getChatAvatar();

  // Mark as read when messages load or change
  useEffect(() => {
    if (messages.length > 0) {
      markAsRead();
    }
  }, [messages.length]);

  // Scroll to bottom on new message or when opening chat
  const scrollToBottom = useCallback((animated = true) => {
    if (flatListRef.current && messages.length > 0) {
      InteractionManager.runAfterInteractions(() => {
        flatListRef.current?.scrollToEnd({ animated });
      });
    }
  }, [messages.length]);

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages.length, scrollToBottom]);

  // Track keyboard height for manual layout (fixes Android residual white space when keyboard closes)
  useEffect(() => {
    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = 'keyboardDidHide';
    const showSub = Keyboard.addListener(showEvent, (e) => {
      const height = e.endCoordinates.height;
      setKeyboardHeight(height);
      // Scroll to bottom so user sees last message while typing
      if (Platform.OS === 'ios') {
        setTimeout(() => scrollToBottom(true), 350);
      } else {
        // Android: layout updates in stages, scroll multiple times to ensure we reach bottom
        setTimeout(() => scrollToBottom(true), 50);
        setTimeout(() => scrollToBottom(true), 200);
        setTimeout(() => scrollToBottom(true), 400);
      }
    });
    const hideSub = Keyboard.addListener(hideEvent, () => {
      setKeyboardHeight(0);
    });
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, [scrollToBottom]);

  const DELETE_TIME_LIMIT_MS = 15 * 60 * 1000; // 15 minutes

  const handleDeleteMessage = useCallback(
    async (messageId: string) => {
      let originalContent: string | undefined;
      updateMessage(messageId, (m) => {
        originalContent = m.content;
        return { ...m, is_deleted: true, content: undefined };
      });
      try {
        await deleteMessage(messageId);
      } catch {
        updateMessage(messageId, (m) => ({ ...m, is_deleted: false, content: originalContent }));
        Alert.alert('Fout', 'Bericht kon niet worden verwijderd.');
      }
    },
    [deleteMessage, updateMessage]
  );

  const handleMessageLongPress = (message: any) => {
    if (message.is_deleted) return;

    const isOwnMessage = message.sender_id === userId;
    const messageAge = Date.now() - new Date(message.created_at).getTime();
    const canDelete = isOwnMessage && messageAge <= DELETE_TIME_LIMIT_MS;

    const options: any[] = [
      {
        text: 'Reageer met emoji',
        onPress: () => setReactionMessage(message),
      },
    ];

    if (canDelete) {
      options.push({
        text: 'Verwijder voor iedereen',
        style: 'destructive',
        onPress: () => {
          Alert.alert(
            'Bericht verwijderen',
            'Weet je zeker dat je dit bericht wilt verwijderen voor iedereen?',
            [
              { text: 'Annuleer', style: 'cancel' },
              {
                text: 'Verwijder',
                style: 'destructive',
                onPress: () => handleDeleteMessage(message.id),
              },
            ]
          );
        },
      });
    }

    options.push({ text: 'Annuleer', style: 'cancel' });

    Alert.alert('Berichtopties', '', options);
  };

  const handleReactionSelect = useCallback(
    async (emoji: string) => {
      if (!reactionMessage) return;
      const messageId = reactionMessage.id;
      setReactionMessage(null);

      const prevReactions = reactionMessage.reactions || [];
      const hasReaction = prevReactions.some(
        (r) => r.emoji === emoji && r.user_id === userId
      );
      const newReactions = hasReaction
        ? prevReactions.filter((r) => !(r.emoji === emoji && r.user_id === userId))
        : [
            ...prevReactions,
            {
              id: `temp-${Date.now()}`,
              message_id: messageId,
              emoji,
              user_id: userId,
              created_at: new Date().toISOString(),
            },
          ];

      updateMessage(messageId, (m) => ({ ...m, reactions: newReactions }));

      try {
        await reactToMessage(messageId, emoji);
      } catch {
        updateMessage(messageId, (m) => ({ ...m, reactions: prevReactions }));
        Alert.alert('Fout', 'Kon reactie niet toevoegen.');
      }
    },
    [reactionMessage, userId, updateMessage, reactToMessage]
  );

  const handleFilePress = async (url: string, type: string) => {
    if (type === 'image') {
      // TODO: Open image viewer
      router.push(`/image-viewer?url=${encodeURIComponent(url)}` as any);
    } else {
      // Open file in browser/external app
      const supported = await Linking.canOpenURL(url);
      if (supported) {
        await Linking.openURL(url);
      } else {
        Alert.alert('Error', 'Cannot open this file');
      }
    }
  };

  const handleAvatarPress = (senderId: string) => {
    router.push(`/profile/user/${senderId}` as any);
  };

  /** Instantly add sent message to the list (optimistic update) */
  const handleMessageSent = useCallback((message: any) => {
    addOptimisticMessage(message);
  }, [addOptimisticMessage]);

  /** Handle swipe-to-reply on a message */
  const handleReply = useCallback((message: Message) => {
    setReplyToMessage(message);
  }, []);

  // Build a lookup map to resolve reply_to locally (Supabase self-joins are unreliable)
  const messagesById = React.useMemo(() => {
    const map = new Map<string, Message>();
    messages.forEach((m) => map.set(m.id, m));
    return map;
  }, [messages]);

  // Group messages by date
  const groupedMessages = groupMessagesByDate(messages);

  if (!userId) {
    return (
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
        <View className="flex-1 items-center justify-center">
          <Text style={{ color: colors.textSecondary }}>
            Log in om chat te bekijken
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  if (loading) {
    return (
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
        <View className="flex-1 items-center justify-center">
          <Loader size={60} />
        </View>
      </SafeAreaView>
    );
  }

  if (error) {
    return (
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }}>
        <View className="flex-1 items-center justify-center px-6">
          <Ionicons name="alert-circle" size={48} color={colors.error} />
          <Text className="text-center mt-4" style={{ color: colors.error }}>{error}</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
    <SafeAreaView 
      edges={['top']} 
      style={{ flex: 1, backgroundColor: colors.background }}
    >
      {/* Playing as dependent banner */}
      {isPlayingAsDependent && (
        <View className="bg-amber-500 px-4 py-2 flex-row items-center justify-center">
          <Ionicons name="person-circle-outline" size={16} color="#FFFFFF" />
          <Text className="text-white text-sm font-medium ml-1.5">
            Je chat als {activeProfile?.nickname || activeProfile?.full_name}
          </Text>
        </View>
      )}

      {/* Custom Header */}
      <View className="px-4 py-3 flex-row items-center" style={{ backgroundColor: colors.primary }}>
        <TouchableOpacity
          onPress={() => router.back()}
          className="mr-3"
          activeOpacity={0.7}
        >
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>

        {/* Avatar and Name */}
        <TouchableOpacity
          onPress={() => {
            if (chat?.type === 'group') {
              router.push({
                pathname: '/chat-screens/group-settings',
                params: { chatId },
              } as any);
            } else {
              router.push({
                pathname: '/chat-detail',
                params: { chatId },
              } as any);
            }
          }}
          className="flex-row items-center flex-1"
          activeOpacity={0.7}
        >
          {chatAvatar ? (
            <Image
              source={{ uri: chatAvatar }}
              className="w-10 h-10 rounded-full mr-3"
              resizeMode="cover"
            />
          ) : (
            <View className="w-10 h-10 rounded-full bg-purple-400 items-center justify-center mr-3">
              <Text className="text-white text-lg font-bold">
                {chatName.charAt(0).toUpperCase()}
              </Text>
            </View>
          )}
          <View className="flex-1">
            <Text className="text-white text-lg font-bold" numberOfLines={1}>
              {chatName}
            </Text>
            {chat?.type === 'group' && chat.participants && (
              <Text className="text-purple-200 text-xs" numberOfLines={1}>
                {chat.participants.filter((p) => !p.left_at).length} deelnemers
              </Text>
            )}
          </View>
        </TouchableOpacity>

        <TouchableOpacity
          onPress={() => {
            if (chat?.type === 'group') {
              router.push({
                pathname: '/chat-screens/group-settings',
                params: { chatId },
              } as any);
            } else {
              router.push({
                pathname: '/chat-detail',
                params: { chatId },
              } as any);
            }
          }}
          className="ml-2"
          activeOpacity={0.7}
        >
          <Ionicons name="information-circle-outline" size={28} color="#fff" />
        </TouchableOpacity>
      </View>

      <View
        style={{
          flex: 1,
          paddingBottom: keyboardHeight,
        }}
      >
        {/* Messages list */}
        <FlatList
          ref={flatListRef}
          data={groupedMessages}
          keyExtractor={(item) => item.date}
          renderItem={({ item }) => (
            <View>
              {/* Date separator */}
              <View className="items-center my-4">
                <View className="px-4 py-1 rounded-full" style={{ backgroundColor: colors.surfaceSecondary }}>
                  <Text className="text-xs" style={{ color: colors.textSecondary }}>
                    {item.date}
                  </Text>
                </View>
              </View>

              {/* Messages for this date */}
              {item.messages.map((message: any, index: number) => {
                const prevMessage = index > 0 ? item.messages[index - 1] : null;
                const showAvatar = !prevMessage || prevMessage.sender_id !== message.sender_id;

                // Resolve reply_to from local messages map
                const enrichedMessage = message.reply_to_id
                  ? { ...message, reply_to: messagesById.get(message.reply_to_id) || message.reply_to }
                  : message;

                  return (
                    <MessageBubble
                      key={message.id}
                      message={enrichedMessage}
                      isOwnMessage={message.sender_id === userId}
                      showAvatar={showAvatar}
                      currentUserId={userId}
                      onLongPress={handleMessageLongPress}
                      onAvatarPress={handleAvatarPress}
                      onFilePress={handleFilePress}
                      onReply={handleReply}
                    />
                  );
              })}
            </View>
          )}
          contentContainerStyle={{
            paddingHorizontal: 16,
            paddingTop: 16,
            paddingBottom: 16,
            flexGrow: 1,
          }}
          onEndReached={() => {
            if (hasMore) {
              loadMore();
            }
          }}
          onEndReachedThreshold={0.5}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor={colors.primary}
              colors={[colors.primary]}
            />
          }
          ListEmptyComponent={
            <View className="flex-1 items-center justify-center py-20">
              <Ionicons name="chatbubbles-outline" size={64} color={colors.textTertiary} />
              <Text className="text-center mt-4" style={{ color: colors.textSecondary }}>
                Nog geen berichten
              </Text>
              <Text className="text-center mt-1" style={{ color: colors.textTertiary }}>
                Zeg hoi! 👋
              </Text>
            </View>
          }
          showsVerticalScrollIndicator={false}
          keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
          keyboardShouldPersistTaps="handled"
          automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
        />

        {/* Typing indicator */}
        {typingText && (
          <View className="px-4 pb-2">
            <Text className="text-xs italic" style={{ color: colors.textSecondary }}>
              {typingText}
            </Text>
          </View>
        )}

        {/* Message input */}
        <MessageInput
          chatId={chatId}
          userId={userId}
          onSendSuccess={scrollToBottom}
          onMessageSent={handleMessageSent}
          onPollPress={() => setShowPollModal(true)}
          replyToMessage={replyToMessage}
          onCancelReply={() => setReplyToMessage(null)}
        />
      </View>

      {/* Poll creation modal */}
      <CreatePollModal
        visible={showPollModal}
        onClose={() => setShowPollModal(false)}
        chatId={chatId}
        userId={userId}
        onPollCreated={() => scrollToBottom()}
      />

      {/* Emoji reaction picker */}
      <EmojiReactionPicker
        visible={!!reactionMessage}
        onClose={() => setReactionMessage(null)}
        onSelect={handleReactionSelect}
      />
    </SafeAreaView>
    </GestureHandlerRootView>
  );
}
